import { useMemo } from "react";
import { dependencyHolds } from "@shared/connectors/fieldRules";
import { logger } from "@/lib/logger";
import type {
  FieldComputedState,
  FieldDefinition,
  FieldDependency,
  FieldStateMap,
  FormValues,
} from "./types";

/**
 * What each field's dependency rules compute to for the current values (FR-40).
 *
 * Ported from skypoint-studio's `useFieldDependencies.ts`. Two changes, both
 * forced by this repository's rules: `console.warn` becomes `logger.warn`
 * (GUIDELINES.md §4 — `console.*` is a lint error), and the warning carries the
 * operator rather than interpolating the field id, because a field id is
 * connector configuration and telemetry dimensions stay id-free.
 *
 * A dependency whose condition does not hold leaves the field at its declared
 * defaults; an unknown operator or effect is ignored rather than guessed at, so
 * a seed written against a newer schema degrades to "the field stays as
 * declared" instead of vanishing.
 */

/** The condition rule is shared with the server's range check (`dependencyHolds`, round 7). */
function evaluateCondition(dependency: FieldDependency, sourceValue: unknown): boolean {
  const { operator, value: expected } = dependency;
  const holds = dependencyHolds(operator, expected, sourceValue);
  if (holds === null) {
    logger.warn("connector form: unknown dependency operator, treated as false", {
      "skydata.dependency_operator": String(operator),
    });
    return false;
  }
  return holds;
}

function declaredState(field: FieldDefinition): FieldComputedState {
  return {
    visible: field.visible,
    disabled: field.disabled,
    required: field.validation.required?.value ?? false,
  };
}

export function useFieldDependencies(
  fieldDefinitions: FieldDefinition[],
  formValues: FormValues,
): FieldStateMap {
  return useMemo(() => {
    const result: FieldStateMap = {};

    for (const field of fieldDefinitions) {
      const state = declaredState(field);

      for (const dependency of field.dependencies ?? []) {
        const sourceValue = formValues[dependency.sourceFieldId];
        if (!evaluateCondition(dependency, sourceValue)) continue;

        switch (dependency.effect.type) {
          case "show":
            state.visible = true;
            break;
          case "hide":
            state.visible = false;
            break;
          case "enable":
            state.disabled = false;
            break;
          case "disable":
            state.disabled = true;
            break;
          case "setRequired":
            state.required = dependency.effect.required;
            break;
          default:
            logger.warn("connector form: dependency effect is not implemented, ignored", {
              "skydata.dependency_effect": dependency.effect.type,
            });
            break;
        }
      }

      result[field.id] = state;
    }

    return result;
  }, [fieldDefinitions, formValues]);
}
